import { ColorList, Colors } from './../../../ui';
import { alea } from 'seedrandom';
import { hash } from '../../../CodesThatsGottaGoSomewhereElse';
import { ComponentTuple } from '../../../concepts/objects';

export type TabularData = any[][];
export type TabularDataTransformer = (data: TabularData) => ComponentTuple[];

export type TabularDataDimension = { key: number, keyLabel: string, label: string };
export const TabularDataDimension = {
    from(data: TabularData[]): TabularDataDimension[] {
        return data[0].map((x, i) => ({
            key: i,
            keyLabel: `Column ${i + 1}`,
            label: x?.toString() || ''
        }));
    }
};

const guessHasHeaders = ([top]: TabularData) => {
    // Any blanks?
    if (top.find(x => !x.trim())) return false;
    // Not all unique?
    if (new Set(top).size !== top.length) return false;
    // Not all have letters
    if (top.filter(x => x.match(/[A-Za-z]/)).length !== top.length) return false;
    // Guess they are
    return true;
};

export type TabularImportParams = Record<string, any> & {
    hasHeaders?: boolean,
    method?: 'clipboard' | 'dragdrop',
    mimeType?: string,
};

export const TabularImportParams = {
    from(data: TabularData): TabularImportParams {
        const hasHeaders = guessHasHeaders(data);
        return { hasHeaders };
    }
};

const colorRandomizedFromValue = (value: string) => {
    const rand = alea(value, { global: true });
    const opts = ColorList.slice(0, -4);
    return opts[Math.floor(rand() * opts.length)];
};

export type MappingFilter = (value: string) => any;
export const MappingFilter = {
    text: (maxLen: number) => (value: string) => value?.trim()?.slice(0, maxLen) ?? '',
    color: (value: string) => Colors.normalize(value) ?? colorRandomizedFromValue(value),
};

export type MappingTarget = { id: string, label: string, path: string, filter: MappingFilter };
export const MappingTarget = (label: string, path: string, filter?: MappingFilter): MappingTarget =>
    ({ id: hash('sha256', path), label, path, filter: filter ?? (x => x) });

export type TabularImportStepCallback = (params: TabularImportParams, advance: number) => void;
export type TabularImportCancelCallback = () => void;

export type TabularImportStepProps = {
    data: TabularData,
    params: TabularImportParams,
    onStep: TabularImportStepCallback,
};

export type TabularImportStep = React.ComponentType<TabularImportStepProps>;

export interface TabularImportStrategy {
    readonly id: string;
    readonly label: string;
    readonly image: string;
    readonly steps: TabularImportStep[];
    apply(data: TabularData, params: TabularImportParams): ComponentTuple[];
}
